import React, { useState, useEffect, useCallback } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import api from '../services/api';
import ChartModal from '../components/masterchef/ChartModal';
import { PositionsPanel } from '../components/masterchef/PositionsPanel';

export default function MasterChef() {
  const { dashboard } = useOutletContext() || {};
  const [livePositions, setLivePositions] = useState([]);
  const [dryPositions, setDryPositions] = useState([]);
  const [liveFilter, setLiveFilter] = useState('active');
  const [dryFilter, setDryFilter] = useState('active');
  const [loading, setLoading] = useState(!dashboard);
  const [error, setError] = useState(null);
  const [chartPosition, setChartPosition] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [liveRes, dryRes] = await Promise.all([
        api.get(`/masterchef/positions?mode=live&status=${liveFilter === 'all' ? '' : liveFilter}&limit=20`),
        api.get(`/masterchef/positions?mode=dry_run&status=${dryFilter === 'all' ? '' : dryFilter}&limit=20`),
      ]);
      setLivePositions(liveRes.data.positions || []);
      setDryPositions(dryRes.data.positions || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch MC data:', err);
      setError('Failed to load Master Chef data');
    } finally {
      setLoading(false);
    }
  }, [liveFilter, dryFilter]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const sellPosition = async (id, percent) => {
    const confirmMsg = percent === 100
      ? 'Close this position (sell 100%)?'
      : `Sell ${percent}% of remaining tokens?`;
    if (!window.confirm(confirmMsg)) return null;
    try {
      const res = await api.post(`/masterchef/positions/${id}/sell`, { percent });
      if (res.data.txHash) {
        alert(`Sold ${percent}%! TX: ${res.data.txHash}\nReceived: ${res.data.amountReceived?.toFixed(6)}`);
      } else if (res.data.amountReceived != null) {
        alert(`Sold ${percent}% (dry run) · received ~${Number(res.data.amountReceived).toFixed(4)} native`);
      }
      await fetchData();
      return res.data.position || null;
    } catch (err) {
      console.error('Failed to sell position:', err);
      alert(err.response?.data?.error || 'Failed to sell position');
      return null;
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading positions...</div>;
  }

  if (error) {
    return <div className="text-center py-12 text-red-600">{error}</div>;
  }

  const d = dashboard || {};
  const exitConfig = d.exitConfig || {};

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Open Positions</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">Live and dry-run positions · daemon status</p>
        </div>
        <Link to="/trading/masterchef" className="text-xs text-blue-500 hover:underline self-start">
          ← Back to overview
        </Link>
      </div>

      {d.processes && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-4">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Daemons</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
            <DaemonCard
              name="Recovery Scanner"
              status={d.processes.recoveryScanner?.status}
              detail={d.processes.recoveryScanner?.status === 'running'
                ? `Every ${d.processes.recoveryScanner.intervalSeconds}s` : 'Stopped'}
              count={d.processes.recoveryScanner?.recoveringTokens || 0}
              sub={`recovering / ${d.processes.recoveryScanner?.totalRecoveries || 0}`}
              color="green"
            />
            <DaemonCard
              name="Position Monitor"
              status={d.processes.positionMonitor?.status}
              detail={d.processes.positionMonitor?.status === 'running'
                ? `Every ${d.processes.positionMonitor.intervalSeconds}s`
                : d.processes.positionMonitor?.status === 'idle' ? 'Idle (no positions)' : 'Stopped'}
              count={d.processes.positionMonitor?.activePositions || 0}
              sub="active positions"
              color={d.processes.positionMonitor?.status === 'idle' ? 'yellow' : 'green'}
            />
            <DaemonCard
              name="Pump Scanner"
              status={d.processes.pumpScanner?.status}
              detail={d.processes.pumpScanner?.status === 'running'
                ? d.processes.pumpScanner.entryMode === 'pullback' ? 'Pullback mode' : 'Immediate mode'
                : 'Stopped'}
              count={d.processes.pumpScanner?.activePumpPositions || 0}
              sub={d.processes.pumpScanner?.pendingSignals > 0
                ? `${d.processes.pumpScanner.pendingSignals} pending` : 'pump positions'}
              color="purple"
            />
            <DaemonCard
              name="Volume Scanner"
              status={d.processes.volumeScanner?.status}
              detail={d.processes.volumeScanner?.status === 'running'
                ? `Every ${d.processes.volumeScanner.intervalSeconds || 30}s` : 'Stopped'}
              count={d.processes.volumeScanner?.activeAlerts || 0}
              sub={`${d.processes.volumeScanner?.activeVolumePositions || 0} positions`}
              color="orange"
            />
            <DaemonCard
              name="EMA Scanner"
              status={d.processes.emaScanner?.status}
              detail={d.processes.emaScanner?.status === 'running'
                ? `Every ${d.processes.emaScanner.intervalSeconds || 180}s` : 'Stopped'}
              count={d.processes.emaScanner?.watchlistCount || 0}
              sub={`${d.processes.emaScanner?.activeEmaPositions || 0} positions`}
              color="cyan"
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PositionsPanel
          title="🟢 Live Trades"
          accentColor="green"
          positions={livePositions}
          filter={liveFilter}
          setFilter={setLiveFilter}
          onChart={setChartPosition}
          analyticsMode="live"
          emptyMsg="No live trade positions yet. Enable live trading in Settings to execute real trades."
        />
        <PositionsPanel
          title="🟡 Dry Run"
          accentColor="yellow"
          positions={dryPositions}
          filter={dryFilter}
          setFilter={setDryFilter}
          onChart={setChartPosition}
          analyticsMode="dry_run"
          emptyMsg="No dry run positions yet. Master Chef will simulate trades when signals match your criteria."
        />
      </div>

      {chartPosition && (
        <ChartModal
          position={chartPosition}
          onClose={() => setChartPosition(null)}
          onSell={sellPosition}
          exitConfig={exitConfig}
          onPositionUpdate={setChartPosition}
        />
      )}
    </div>
  );
}

function DaemonCard({ name, status, detail, count, sub, color }) {
  const dotColor = status === 'running'
    ? (color === 'purple' ? 'bg-purple-500 animate-pulse'
      : color === 'orange' ? 'bg-orange-500 animate-pulse'
        : color === 'cyan' ? 'bg-cyan-500 animate-pulse'
          : 'bg-green-500 animate-pulse')
    : status === 'idle' ? 'bg-yellow-400' : 'bg-gray-300';

  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
      <div className="flex items-center gap-3">
        <div className={`w-2.5 h-2.5 rounded-full ${dotColor}`} />
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-white">{name}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{detail}</p>
        </div>
      </div>
      <div className="text-right">
        <p className="text-lg font-bold text-gray-900 dark:text-white">{count}</p>
        <p className="text-xs text-gray-400 dark:text-gray-500">{sub}</p>
      </div>
    </div>
  );
}
